import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import { getClaims, reviewClaim, getMemberships, changeMemberRole, addMember } from '../services/membershipService';
import { getProjects, createProject } from '../services/projectService';
import { updateTenantBranding, updateModuleConfig, updateTenantSettings } from '../services/tenantService';
import CommunityHomeEditor from '../components/CommunityHomeEditor';
import { uploadTraditionalLands } from '../services/geographyService';
import {
  getPolygons as getStewardshipPolygons,
  approvePolygon as approveStewardshipPolygon,
  archivePolygon as archiveStewardshipPolygon,
} from '../services/stewardshipService';
import GeographyLayersTab from '../components/geography/GeographyLayersTab';
import StaffSectionAccess from '../components/StaffSectionAccess';
import { STAFF_SECTIONS } from '../utils/staffAccess';

const STATUS_LABELS = {
  unverified:          'Unverified',
  self_declared:       'Self-Declared',
  community_pending:   'Pending',
  community_verified:  'Verified',
  revoked:             'Revoked',
  // CommunityMembership statuses
  pending:   'Pending',
  active:    'Active',
  suspended: 'Suspended',
};

// ─── ProjectsTab ──────────────────────────────────────────────────────────────
const ProjectsTab = ({ tenantId }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState({ title: '', description: '', type: 'public' });
  const [msg, setMsg]           = useState('');

  useEffect(() => {
    getProjects(tenantId)
      .then((r) => setProjects(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await createProject({ ...form, tenantId });
      setProjects((prev) => [res.data, ...prev]);
      setForm({ title: '', description: '', type: 'public' });
      setMsg('Project created.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create project');
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  return (
    <section>
      <h3>Create Project</h3>
      {msg && <p className={msg.includes('Failed') ? 'error-msg' : 'success-msg'}>{msg}</p>}
      <form onSubmit={handleSubmit}>
        <input
          name="title"
          placeholder="Project Title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
        <textarea
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <select
          name="type"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          <option value="public">Public</option>
          <option value="community">Community</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <button type="submit">Create Project</button>
      </form>

      <h3>All Projects</h3>
      {projects.length === 0 ? (
        <p>No projects yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Title</th><th>Type</th><th>Status</th></tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p._id}>
                <td>{p.title}</td>
                <td>{p.type}</td>
                <td>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── MembershipTab ────────────────────────────────────────────────────────────
export const MembershipTab = ({ tenantId }) => {
  const [claims, setClaims]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [reviewError, setError]   = useState('');
  const [roles, setRoles] = useState({});
  const [saving, setSaving] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setClaims([]);
    setError('');
    setRoles({});
    getClaims({ tenantId })
      .then((r) => { if (current) setClaims(r.data); })
      .catch((err) => { if (current) setError(err.response?.data?.message || 'Unable to load registrations. Please retry.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [tenantId, revision]);

  const handleReview = async (claimId, statusAssigned) => {
    setError('');
    setSaving(claimId);
    try {
      const res = await reviewClaim(claimId, {
        statusAssigned,
        ...(statusAssigned === 'community_verified' ? { communityRole: roles[claimId] || 'member' } : {}),
      });
      setClaims((prev) => prev.map((c) => (c._id === claimId
        ? { ...c, ...res.data.claim, userId: c.userId } : c)));
    } catch (err) {
      setError(err.response?.data?.message || 'Review failed');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  return (
    <section>
      <h3>Registrations ({claims.length})</h3>
      <p>Approve applicants and choose their community role. For staff, assign departments in Members after approval.</p>
      {reviewError && <div className="error-msg" role="alert">{reviewError}
        <button onClick={() => setRevision((value) => value + 1)}>Reload registrations</button></div>}
      {claims.length === 0 ? (
        !reviewError && <p>No registrations yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Person</th><th>Status</th><th>Submitted</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {claims.map((c) => {
              const status = c.resolvedStatus || c.status;
              return (
                <tr key={c._id}>
                  <td>{c.userId?.email || c.userId?._id || c.userId}
                    {c.supportingInfo?.notes && <p>{c.supportingInfo.notes}</p>}</td>
                  <td><span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span></td>
                  <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td className="claim-actions">
                    {status !== 'community_verified' && status !== 'active' && (
                      <><label>Community role
                        <select aria-label={`Community role for ${c.userId?.email || c.userId?._id || c.userId}`}
                          value={roles[c._id] || 'member'} disabled={saving !== null}
                          onChange={(event) => setRoles((prev) => ({ ...prev, [c._id]: event.target.value }))}>
                          <option value="member">Member</option>
                          <option value="community_staff">Community staff</option>
                          <option value="community_admin">Community administrator</option>
                        </select>
                      </label>
                      <button disabled={saving !== null} onClick={() => handleReview(c._id, 'community_verified')}>
                        {saving === c._id ? 'Saving…' : 'Approve'}
                      </button></>
                    )}
                    {status !== 'revoked' && (
                      <button disabled={saving !== null} className="btn-danger" onClick={() => handleReview(c._id, 'revoked')}>Revoke</button>
                    )}
                    {status !== 'community_pending' && status !== 'pending' && (
                      <button disabled={saving !== null} className="btn-secondary" onClick={() => handleReview(c._id, 'community_pending')}>Set Pending</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── ModuleConfigTab ──────────────────────────────────────────────────────────
const MODULES = [
  { key: 'consultation',  label: 'Consultation',    description: 'Community questionnaires and feedback consultations' },
  { key: 'communityNews', label: 'Community News',  description: 'News posts and community announcements' },
  { key: 'coms',          label: 'Coms',            description: 'Community communications workspace and thread tools' },
  { key: 'harvesting',    label: 'Harvesting',      description: 'Harvest observation records and maps' },
  { key: 'communityData', label: 'Community Data',  description: 'Community-submitted data and consent exports' },
  { key: 'landValues',    label: 'Land Values',     description: 'Community land value criteria and scoring' },
  { key: 'landUnits',     label: 'Land Units',      description: 'Land unit assessments and evaluations' },
];

const ModuleConfigTab = ({ tenantId, activeTenant }) => {
  const mc = activeTenant?.moduleConfig || {};

  const [config, setConfig] = useState(() => {
    const init = {};
    MODULES.forEach(({ key }) => { init[key] = mc[key]?.enabled ?? false; });
    return init;
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleToggle      = (key) => setConfig((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setMsg('');
    setSaving(true);
    try {
      // Build nested per-module objects so the backend deep-merges correctly,
      // preserving externalUrl and other fields not managed here.
      const moduleConfig = {};
      MODULES.forEach(({ key }) => {
        moduleConfig[key] = { enabled: config[key] };
      });
      await updateModuleConfig(tenantId, { moduleConfig });
      setMsg('Module settings saved.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <h3>Module Configuration</h3>
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
      {msg && <p className={msg.includes('aved') ? 'success-msg' : 'error-msg'}>{msg}</p>}

      {/* ── Module on/off ── */}
      <p style={{ color: '#64748b', marginBottom: 16, fontSize: '0.9rem' }}>
        Enable or disable modules for your community members.
        Platform-level tools (Lenses, 7 Generation) are always available to CIP staff regardless of these settings.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {MODULES.map(({ key, label, description }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
            <input
              type="checkbox"
              checked={config[key]}
              onChange={() => handleToggle(key)}
              style={{ marginTop: 3, accentColor: '#4285F4', width: 16, height: 16 }}
            />
            <div>
              <strong style={{ fontSize: '0.95rem' }}>{label}</strong>
              <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>{description}</p>
            </div>
          </label>
        ))}
      </div>

      <div style={{ marginTop: 28 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: '0.97rem', color: '#1e293b' }}>Staff work sections</h4>
        <p style={{ color: '#64748b', marginBottom: 12, fontSize: '0.85rem' }}>
          Assign individual staff access in <strong>Members → Staff sections</strong>.
          Module switches above control community availability; staff section assignments control
          who manages each enabled section. Community admins retain all community sections.
        </p>
      </div>
    </div>
  );
};

// ─── BrandingTab ──────────────────────────────────────────────────────────────
const BrandingTab = ({ tenantId, activeTenant }) => {
  const [form, setForm] = useState({
    displayName:  activeTenant?.branding?.displayName  || '',
    logoUrl:      activeTenant?.branding?.logoUrl       || '',
    primaryColor: activeTenant?.branding?.primaryColor  || '',
    bannerText:   activeTenant?.branding?.bannerText    || '',
    websiteUrl:   activeTenant?.branding?.websiteUrl    || '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setSaving(true);
    try {
      await updateTenantBranding(tenantId, form);
      setMsg('Branding updated successfully.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h3>Community Branding</h3>
      <p className="fieldset-hint">
        These settings control how your community appears to members and on auto-generated join pages.
      </p>
      <form className="branding-form" onSubmit={handleSubmit}>
        <label>
          Display Name
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder={activeTenant?.name}
          />
          <small>Overrides the technical tenant name in all UI.</small>
        </label>
        <label>
          Logo URL
          <input
            type="url"
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://..."
          />
        </label>
        {form.logoUrl && (
          <img src={form.logoUrl} alt="Logo preview" style={{ maxHeight: 80, maxWidth: 300, objectFit: 'contain', marginBottom: 12 }} />
        )}
        <label>
          Primary Colour (hex)
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={form.primaryColor}
              onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
              placeholder="#2B6CB0"
              style={{ flex: 1 }}
            />
            {form.primaryColor && (
              <span
                style={{
                  display: 'inline-block',
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: form.primaryColor,
                  border: '1px solid #ccc',
                }}
              />
            )}
          </div>
        </label>
        <label>
          Banner Text
          <input
            type="text"
            value={form.bannerText}
            onChange={(e) => setForm({ ...form, bannerText: e.target.value })}
            placeholder="Welcome to our community portal"
          />
        </label>
        <label>
          Website URL
          <input
            type="url"
            value={form.websiteUrl}
            onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
            placeholder="https://your-community-website.com"
          />
          <small>Link users back to your main community website.</small>
        </label>
        {msg && (
          <p className={msg.includes('successfully') ? 'success-msg' : 'error-msg'}>{msg}</p>
        )}
        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Branding'}
          </button>
        </div>
      </form>
    </section>
  );
};

// ─── MembersTab ───────────────────────────────────────────────────────────────
const ROLE_LABELS = { member: 'Member', community_staff: 'Staff', community_admin: 'Admin' };
const STATUS_BADGE_MAP = { active: 'status-active', pending: 'status-pending', suspended: 'status-suspended', revoked: 'status-revoked' };

export const MembersTab = ({ tenantId, canManageRoles = true }) => {
  const [members, setMembers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [msg, setMsg]                 = useState('');
  const [roleEdits, setRoleEdits]     = useState({});   // membershipId -> selected role
  const [roleSaving, setRoleSaving]   = useState({});   // membershipId -> bool

  // Add Member form state (community_admin only)
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', firstName: '', lastName: '', communityRole: 'member', tempPassword: '' });
  const [addMsg, setAddMsg]   = useState('');
  const [adding, setAdding]   = useState(false);

  useEffect(() => {
    getMemberships({ tenantId })
      .then((r) => setMembers(r.data))
      .catch((error) => setMsg(error.response?.data?.message || 'Could not load community members.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleRoleChange = (membershipId, newRole) => {
    setRoleEdits((prev) => ({ ...prev, [membershipId]: newRole }));
  };

  const handleRoleSave = async (membershipId) => {
    const newRole = roleEdits[membershipId];
    setMsg('');
    setRoleSaving((prev) => ({ ...prev, [membershipId]: true }));
    try {
      const res = await changeMemberRole(membershipId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m._id === membershipId ? { ...m, communityRole: res.data.communityRole, staffSections: res.data.staffSections || [] } : m))
      );
      setRoleEdits((prev) => { const n = { ...prev }; delete n[membershipId]; return n; });
    } catch (err) {
      setMsg(err.response?.data?.message || 'Role change failed');
    } finally {
      setRoleSaving((prev) => ({ ...prev, [membershipId]: false }));
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddMsg('');
    if (addForm.tempPassword.length < 8) { setAddMsg('Temporary password must be at least 8 characters.'); return; }
    setAdding(true);
    try {
      const res = await addMember({ tenantId, ...addForm });
      setMembers((prev) => [res.data.membership, ...prev]);
      setAddForm({ email: '', firstName: '', lastName: '', communityRole: 'member', tempPassword: '' });
      setShowAdd(false);
      setMsg('');
      setAddMsg('');
    } catch (err) {
      setAddMsg(err.response?.data?.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const active  = members.filter((m) => m.status === 'active');
  const pending = members.filter((m) => m.status === 'pending');
  const other   = members.filter((m) => !['active', 'pending'].includes(m.status));

  return (
    <section>
      <div className="section-header">
        <h3>Community Members ({members.length})</h3>
        {canManageRoles && (
          <button onClick={() => { setShowAdd((v) => !v); setAddMsg(''); }}>
            {showAdd ? 'Cancel' : '+ Add Member'}
          </button>
        )}
      </div>
      {msg && <p className="error-msg">{msg}</p>}

      {showAdd && canManageRoles && (
        <form onSubmit={handleAddMember} style={{ marginBottom: 20 }}>
          <fieldset>
            <legend>Add Member</legend>
            <p className="fieldset-hint">
              Creates an account (or reuses an existing one by email) and adds it to this community
              with the chosen role. Share the temporary password with the person securely.
            </p>
            <label>Email
              <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required />
            </label>
            <label>First name
              <input value={addForm.firstName} onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })} />
            </label>
            <label>Last name
              <input value={addForm.lastName} onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })} />
            </label>
            <label>Role
              <select value={addForm.communityRole} onChange={(e) => setAddForm({ ...addForm, communityRole: e.target.value })}>
                <option value="member">Member</option>
                <option value="community_staff">Staff</option>
                <option value="community_admin">Admin</option>
              </select>
            </label>
            <label>Temporary password
              <input type="password" value={addForm.tempPassword} onChange={(e) => setAddForm({ ...addForm, tempPassword: e.target.value })} minLength={8} required autoComplete="new-password" />
            </label>
            {addMsg && <p className="error-msg">{addMsg}</p>}
            <div className="form-actions">
              <button type="submit" disabled={adding}>{adding ? 'Adding…' : 'Add Member'}</button>
            </div>
          </fieldset>
        </form>
      )}

      {pending.length > 0 && (
        <div className="pending-section">
          <h4>⏳ Pending ({pending.length})</h4>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Since</th></tr></thead>
            <tbody>
              {pending.map((m) => (
                <tr key={m._id}>
                  <td>{m.userId?.email}</td>
                  <td>{m.userId?.firstName} {m.userId?.lastName}</td>
                  <td>{ROLE_LABELS[m.communityRole] || m.communityRole}</td>
                  <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="fieldset-hint">
            To approve pending members, use the <strong>Registrations</strong> tab.
          </p>
        </div>
      )}

      <h4>Active Members ({active.length})</h4>
      {members.some((member) => member.userId?.identityUnavailable) && (
        <p role="alert">Some names or email addresses cannot be read with this server's encryption key.
          Contact the platform administrator to repair the PII key configuration. Member records and access are unchanged.</p>
      )}
      {canManageRoles && <p>Admins retain all community sections. Staff need explicit section assignments below;
        existing staff with no assignments have contributor access only. Assign their work sections before rolling out this version.</p>}
      {active.length === 0 ? (
        <p>No active members yet.</p>
      ) : (
        <div className="community-members">
            {active.map((m) => {
              const editing = roleEdits[m._id] !== undefined;
              const currentRole = roleEdits[m._id] ?? m.communityRole;
              const name = [m.userId?.firstName, m.userId?.lastName].filter(Boolean).join(' ');
              const sections = STAFF_SECTIONS.filter(({ key }) => m.staffSections?.includes(key));
              const accessSummary = m.communityRole === 'community_admin' ? 'All community sections'
                : m.communityRole === 'community_staff' ? sections.map(({ label }) => label.split(' - ')[0]).join(', ') || 'No staff sections assigned'
                  : 'Own contributor access';
              return (
                <details key={m._id} className="community-member" aria-label={name || m.userId?.email || 'Community member'}>
                  <summary>
                    <span className="community-member__line">
                      <span className="community-member__identity"><strong>{name || m.userId?.email || 'Community member'}</strong>
                        {name && <small>{m.userId?.email}</small>}</span>
                      <span className="status-badge">
                      {ROLE_LABELS[m.communityRole] || m.communityRole}
                      </span>
                      <span className="community-member__access">{accessSummary}</span>
                    </span>
                  </summary>
                  <div className="community-member__settings">
                  <p className="fieldset-hint">Joined: {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : 'Not recorded'}</p>
                  {canManageRoles && (
                  <div className="community-member__role">
                    <label>Community role
                    <select
                      value={currentRole}
                      onChange={(e) => handleRoleChange(m._id, e.target.value)}
                      disabled={roleSaving[m._id]}
                    >
                      <option value="member">Member</option>
                      <option value="community_staff">Staff</option>
                      <option value="community_admin">Admin</option>
                    </select>
                    </label>
                    {editing && (
                      <button
                        className="btn-sm"
                        disabled={roleSaving[m._id]}
                        onClick={() => handleRoleSave(m._id)}
                      >
                        {roleSaving[m._id] ? 'Saving…' : 'Save role'}
                      </button>
                    )}
                  </div>
                  )}
                  {canManageRoles && <>
                    <p className="fieldset-hint">Members manage their own contributions. Staff manage selected work sections.
                      Admins manage the community and all sections. Save a role change before assigning sections.</p>
                    {m.communityRole === 'community_staff' ? <StaffSectionAccess key={`${m._id}:${m.communityRole}`}
                      membership={m} onSaved={(updated) => setMembers((prev) => prev.map((entry) =>
                        entry._id === m._id ? { ...entry, staffSections: updated.staffSections } : entry))} />
                      : m.communityRole === 'community_admin' ? 'All community sections' : 'Own contributor access'}
                  </>}
                  </div>
                </details>
              );
            })}
        </div>
      )}

      {other.length > 0 && (
        <>
          <h4>Other ({other.length})</h4>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Status</th></tr></thead>
            <tbody>
              {other.map((m) => (
                <tr key={m._id}>
                  <td>{m.userId?.email}</td>
                  <td><span className={`status-badge ${STATUS_BADGE_MAP[m.status] || ''}`}>{m.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

// ─── SettingsTab ─────────────────────────────────────────────────────────────
const SettingsTab = ({ tenantId, activeTenant }) => {
  const [listed, setListed] = useState(activeTenant?.settings?.listInDirectory ?? false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await updateTenantSettings(tenantId, { listInDirectory: listed });
      setMsg('Settings saved.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const slug = activeTenant?.slug || '';
  const joinUrl = slug ? `${window.location.origin}/t/${slug}/join` : null;

  return (
    <div>
      <div className="section-header">
        <h3>Community Settings</h3>
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
      {msg && <p className={msg.includes('aved') ? 'success-msg' : 'error-msg'}>{msg}</p>}

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: '0.97rem', color: '#1e293b' }}>Public Directory Listing</h4>
        <p style={{ color: '#64748b', marginBottom: 12, fontSize: '0.85rem' }}>
          When enabled, your community appears in the public directory on the Membership page.
          Anyone can find and apply to join. Disable to keep your community private —
          members join via your direct link below.
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
          <input
            type="checkbox"
            checked={listed}
            onChange={e => setListed(e.target.checked)}
            style={{ marginTop: 3, accentColor: '#4285F4', width: 16, height: 16 }}
          />
          <div>
            <strong style={{ fontSize: '0.95rem' }}>List in public directory</strong>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
              {listed
                ? 'Your community is visible in the public directory'
                : 'Your community is private — share your join link directly with members'}
            </p>
          </div>
        </label>
      </div>

      {joinUrl && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: '0.97rem', color: '#1e293b' }}>Direct Join Link</h4>
          <p style={{ color: '#64748b', marginBottom: 8, fontSize: '0.85rem' }}>
            Share this link with your members. Anyone with the link can apply to join.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{
              background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6,
              padding: '6px 12px', fontSize: '0.82rem', wordBreak: 'break-all', flex: 1,
            }}>{joinUrl}</code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(joinUrl); }}
              style={{
                background: '#4285F4', border: 'none', borderRadius: 6,
                color: '#fff', padding: '6px 14px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
              }}
            >
              Copy Link
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 6 }}>
            Community slug: <strong>{slug}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

// ─── TraditionalLandsList ─────────────────────────────────────────────────────
const TRAD_STATUS_STYLE = {
  approved: { background: '#dcfce7', color: '#166534' },
  draft:    { background: '#fef3c7', color: '#92400e' },
  archived: { background: '#f1f5f9', color: '#64748b' },
};

export const TraditionalLandsList = ({ tenantId, canApprove = false, reloadKey = 0 }) => {
  const [polygons, setPolygons] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busyId, setBusyId]     = useState(null);
  const [error, setError]       = useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getStewardshipPolygons(tenantId, { geometryType: 'traditional_land', limit: 100 });
      setPolygons(res.data?.polygons || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load traditional lands.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const act = async (polygon, action) => {
    setBusyId(polygon._id);
    setError('');
    try {
      if (action === 'approve') await approveStewardshipPolygon(tenantId, polygon._id);
      else await archiveStewardshipPolygon(tenantId, polygon._id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const approvedCount = polygons.filter((p) => p.status === 'approved').length;
  const btn = (bg) => ({ padding: '5px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, marginLeft: 6 });

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 24, marginBottom: 24, maxWidth: 760 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 15, color: '#1e293b' }}>Imported boundaries</h4>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px' }}>
        Only <strong>approved</strong> boundaries appear on the community map ({approvedCount} of {polygons.length} approved).
        {!canApprove && ' A community admin must approve them.'}
      </p>
      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {loading ? <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p> : !polygons.length ? (
        <p style={{ fontSize: 13, color: '#64748b' }}>No traditional land boundaries imported yet.</p>
      ) : (
        <table className="admin-table" style={{ width: '100%' }}>
          <thead><tr><th>Name</th><th>Status</th><th>Imported</th><th /></tr></thead>
          <tbody>
            {polygons.map((p) => (
              <tr key={p._id}>
                <td>{p.name}</td>
                <td>
                  <span style={{ ...(TRAD_STATUS_STYLE[p.status] || {}), borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    {p.status === 'approved' ? 'Published' : p.status}
                  </span>
                </td>
                <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canApprove && p.status !== 'approved' && (
                    <button type="button" style={btn('#16a34a')} disabled={busyId === p._id} onClick={() => act(p, 'approve')}>
                      Approve &amp; publish
                    </button>
                  )}
                  {canApprove && p.status !== 'archived' && (
                    <button type="button" style={btn('#64748b')} disabled={busyId === p._id} onClick={() => act(p, 'archive')}>
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── TraditionalLandsTab ──────────────────────────────────────────────────────
export const TraditionalLandsTab = ({ tenantId, canApprove = false }) => {
  const fileRef     = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [file,      setFile]      = useState(null);
  const [nameField, setNameField] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);  // { message, created, skipped, errors }
  const [preview,   setPreview]   = useState(null);  // { total, withGeo, withoutGeo }

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); setPreview(null); return; }
    setFile(f);
    setResult(null);
    // Quick preview parse
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const gj = JSON.parse(ev.target.result);
        const features = gj.features || [];
        const withGeo = features.filter((ft) =>
          ft.geometry && ['Polygon','MultiPolygon'].includes(ft.geometry.type) && ft.geometry.coordinates?.length
        ).length;
        setPreview({ total: features.length, withGeo, withoutGeo: features.length - withGeo });
      } catch {
        setPreview(null);
      }
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('nameField', nameField);
      const res = await uploadTraditionalLands(tenantId, fd);
      setResult(res.data);
      setReloadKey((k) => k + 1);
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setResult({
        message: err.response?.data?.message || 'Upload failed. Please check the file and try again.',
        errors: err.response?.data?.errors || [],
      });
    } finally {
      setUploading(false);
    }
  };

  const S = {
    card:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 24, marginBottom: 24, maxWidth: 640 },
    label:   { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
    input:   { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box', marginBottom: 14 },
    btn:     (bg, fg) => ({ padding: '9px 22px', background: bg || '#2563eb', color: fg || '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700 }),
    preview: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 7, padding: '10px 14px', marginBottom: 14, fontSize: 13 },
    success: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '10px 14px', marginBottom: 14, color: '#166534', fontSize: 13 },
    error:   { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 7, padding: '10px 14px', marginBottom: 14, color: '#b91c1c', fontSize: 13 },
  };

  const isSuccess = result?.created > 0;

  return (
    <div>
      <h3 style={{ fontSize: 17, margin: '0 0 6px' }}>Community Traditional Lands</h3>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24, maxWidth: 560 }}>
        Upload a GeoJSON file containing your community's traditional territory boundaries.
        Each feature with a Polygon or MultiPolygon geometry will be created as a draft stewardship area
        in the <strong>Traditional Lands</strong> category. Review the imported names and approve
        the intended territory below to display it on all harvest maps.
      </p>

      <div style={S.card}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, color: '#1e293b' }}>Upload GeoJSON File</h4>

        <label style={S.label}>GeoJSON / JSON File</label>
        <input
          ref={fileRef}
          type="file"
          accept=".json,.geojson,application/json"
          onChange={handleFileChange}
          style={{ display: 'block', marginBottom: 14, fontSize: 13, cursor: 'pointer' }}
        />

        {preview && (
          <div style={S.preview}>
            <strong>{preview.total.toLocaleString()} features found</strong>
            {' — '}
            <span style={{ color: '#0369a1' }}>{preview.withGeo.toLocaleString()} with polygon geometry</span>
            {preview.withoutGeo > 0 && (
              <span style={{ color: '#64748b' }}> · {preview.withoutGeo.toLocaleString()} without geometry (will be skipped)</span>
            )}
          </div>
        )}

        <label style={S.label}>Name Property Field</label>
        <input
          type="text"
          value={nameField}
          onChange={(e) => setNameField(e.target.value)}
          placeholder="Automatic - or enter a field such as Name"
          style={S.input}
        />
        <p style={{ fontSize: 11, color: '#94a3b8', marginTop: -10, marginBottom: 14 }}>
          Leave blank to detect the territory name field. Field matching ignores capitalization, so Name and name both work.
        </p>

        <button
          style={{ ...S.btn(), opacity: (!file || uploading) ? 0.6 : 1 }}
          disabled={!file || uploading}
          onClick={handleUpload}
        >
          {uploading ? 'Uploading…' : '⬆ Import Traditional Lands'}
        </button>
      </div>

      {result && (
        <div style={isSuccess ? S.success : S.error}>
          <strong>{result.message}</strong>
          {result.errors?.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {isSuccess && (
            <p style={{ margin: '8px 0 0', fontSize: 12 }}>
              The imported areas are in <em>draft</em> status. Approve them below to show them on the community map.
            </p>
          )}
        </div>
      )}

      <TraditionalLandsList tenantId={tenantId} canApprove={canApprove} reloadKey={reloadKey} />

      <div style={{ maxWidth: 560 }}>
        <h4 style={{ fontSize: 14, color: '#1e293b', marginBottom: 10 }}>Tips</h4>
        <ul style={{ fontSize: 13, color: '#475569', paddingLeft: 18, lineHeight: 1.7 }}>
          <li>File must be a <strong>GeoJSON FeatureCollection</strong> with Polygon or MultiPolygon features.</li>
          <li>Features without geometry (points, null) are skipped — they won't appear in stewardship.</li>
          <li>Duplicate uploads will create new draft records; use the Stewardship page to manage or delete them.</li>
          <li>Coordinates must be in <strong>WGS 84 (EPSG:4326)</strong> — standard longitude/latitude.</li>
        </ul>
      </div>
    </div>
  );
};

// ─── CommunityAdminPage ───────────────────────────────────────────────────────
const CommunityAdminPage = () => {
  // Works inside TenantProvider (/t/:slug/community-admin) and standalone (/community-admin)
  const tenantCtx = useTenantSafe();
  const { activeTenant, access, panel, loadSession, user } = useAuth();

  const viewTenant    = tenantCtx?.viewTenant || activeTenant;
  const tenantId      = viewTenant?._id;
  const tenantLoading = tenantCtx ? tenantCtx.loading : false;
  const tenantError   = tenantCtx ? tenantCtx.error : null;

  const isPlatformStaff = ['admin', 'staff'].includes(panel);
  const communityRole   = tenantId ? access?.communityRoles?.[tenantId] : null;
  const isAdmin = isPlatformStaff || communityRole === 'community_admin';
  const canEditHome = user?.globalRole === 'cip_admin' || communityRole === 'community_admin';
  const canManageGeography = isAdmin;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ['projects', 'members', 'membership', 'branding', 'modules', 'settings', 'geography', 'traditional'].includes(searchParams.get('tab'))
    ? searchParams.get('tab') : 'projects';
  const setActiveTab = (tab) => setSearchParams({ tab });

  if (tenantLoading) {
    return (
      <div className="page community-admin-page">
        <p className="page-loading">Loading community…</p>
      </div>
    );
  }

  if (tenantError || !tenantId) {
    return (
      <div className="page community-admin-page">
        <p className="page-loading">{tenantError || 'Community not found.'}</p>
      </div>
    );
  }

  if (!isAdmin) return <p role="alert">Community administrator access required. Use Stewardship for species guidance and planning.</p>;
  const visibleTab = activeTab;

  return (
    <div className="page community-admin-page">
      <h2>Community Admin — {viewTenant?.branding?.displayName || viewTenant?.name}</h2>

      <div className="tabs">
        {isAdmin && <><button className={activeTab === 'projects'   ? 'active' : ''} onClick={() => setActiveTab('projects')}>
          Projects
        </button>
        <button className={activeTab === 'members'    ? 'active' : ''} onClick={() => setActiveTab('members')}>
          Members
        </button>
        <button className={activeTab === 'membership' ? 'active' : ''} onClick={() => setActiveTab('membership')}>
          Registrations
        </button></>}
        {isAdmin && (
          <>
            <button className={activeTab === 'branding'   ? 'active' : ''} onClick={() => setActiveTab('branding')}>
              Branding
            </button>
            <button className={activeTab === 'modules'   ? 'active' : ''} onClick={() => setActiveTab('modules')}>
              Modules
            </button>
            <button className={activeTab === 'settings'  ? 'active' : ''} onClick={() => setActiveTab('settings')}>
              Settings
            </button>
          </>
        )}
        {canManageGeography && (
          <button className={visibleTab === 'geography' ? 'active' : ''} onClick={() => setActiveTab('geography')}>
            Geography Layers
          </button>
        )}
        {canManageGeography && (
          <button className={activeTab === 'traditional' ? 'active' : ''} onClick={() => setActiveTab('traditional')}>
            Traditional Lands
          </button>
        )}
      </div>

      {isAdmin && visibleTab === 'projects'   && <ProjectsTab      tenantId={tenantId} />}
      {isAdmin && visibleTab === 'members'    && <MembersTab key={tenantId} tenantId={tenantId} canManageRoles={isAdmin} />}
      {isAdmin && visibleTab === 'membership' && <MembershipTab key={tenantId} tenantId={tenantId} />}
      {isAdmin && activeTab === 'branding' && <>
        {canEditHome && <CommunityHomeEditor key={tenantId} tenant={viewTenant} onSaved={(tenant) => {
          tenantCtx?.updateViewTenant?.(tenant);
          loadSession();
        }} />}
        <BrandingTab tenantId={tenantId} activeTenant={viewTenant} />
      </>}
      {isAdmin && activeTab === 'modules'    && <ModuleConfigTab   tenantId={tenantId} activeTenant={viewTenant} />}
      {canManageGeography && visibleTab === 'geography' && (
        <GeographyLayersTab tenantId={tenantId} isPlatformStaff={isPlatformStaff} />
      )}
      {canManageGeography && visibleTab === 'traditional' && (
        <TraditionalLandsTab tenantId={tenantId} canApprove={canManageGeography} />
      )}
      {isAdmin && activeTab === 'settings'   && <SettingsTab       tenantId={tenantId} activeTenant={viewTenant} />}
    </div>
  );
};

export default CommunityAdminPage;
